import {
  PropsWithChildren,
  ReactNode,
  Fragment,
  ComponentPropsWithoutRef,
} from "react";
import {
  DropdownMenu as DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuIcon,
  DropdownMenuGroup,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

function Trigger({ children }: PropsWithChildren) {
  return <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>;
}

export interface DropdownMenuItem {
  label: ReactNode;
  onClick: (event: React.MouseEvent) => void;
  icon?: ReactNode;
  active?: boolean;
}

export interface DropdownMenuSeparator {
  type: "separator";
}

export interface DropdownMenuGroup {
  items: (DropdownMenuItem | DropdownMenuGroup | DropdownMenuSeparator)[];
  label?: ReactNode;
  icon?: ReactNode;
}

type MenuItemProps = DropdownMenuItem;

function MenuItem({ label, onClick, icon, active }: MenuItemProps) {
  return (
    <DropdownMenuItem onClick={onClick} className={cn(active && "bg-muted")}>
      {icon && <DropdownMenuIcon>{icon}</DropdownMenuIcon>}
      {label}
    </DropdownMenuItem>
  );
}

function MenuSeparator() {
  return <DropdownMenuSeparator />;
}

function MenuGroup({ label, items, icon }: DropdownMenuGroup) {
  return (
    <DropdownMenuGroup>
      {label && (
        <DropdownMenuLabel>
          {icon}
          {label}
        </DropdownMenuLabel>
      )}

      {items?.map((item, index) => {
        if ("items" in item) {
          return <MenuGroup key={index} {...item} />;
        }

        if ("onClick" in item) {
          return <MenuItem key={index} {...item} />;
        }

        if ("type" in item && item.type === "separator") {
          return <MenuSeparator key={index} />;
        }
      })}
    </DropdownMenuGroup>
  );
}

export interface DropdownMenuProps
  extends Pick<
    ComponentPropsWithoutRef<typeof DropdownMenuContent>,
    "side" | "align"
  > {
  items: (DropdownMenuItem | DropdownMenuGroup | DropdownMenuSeparator)[];
  portal?: boolean;
}

function DropdownMenu({
  children,
  items,
  portal = true,
  side = "bottom",
  align = "start",
}: PropsWithChildren<DropdownMenuProps>) {
  const Wrapper = portal ? DropdownMenuPortal : Fragment;

  return (
    <DropdownMenuRoot>
      {children}

      <Wrapper>
        <DropdownMenuContent
          align={align}
          side={side}
          sideOffset={4}
          collisionPadding={16}
        >
          {items?.map((item, index) => {
            if ("items" in item) {
              return <MenuGroup key={index} {...item} />;
            }

            if ("onClick" in item) {
              return <MenuItem key={index} {...item} />;
            }

            if ("type" in item && item.type === "separator") {
              return <MenuSeparator key={index} />;
            }
          })}
        </DropdownMenuContent>
      </Wrapper>
    </DropdownMenuRoot>
  );
}

DropdownMenu.Trigger = Trigger;

export default DropdownMenu;
